import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { retry, throwError, timer } from 'rxjs';
import { isApiRequest } from './api-request.guard';

const MAX_RETRIES = 3;
const MAX_BACKOFF_MS = 8000;

// Transient infra failures worth retrying. 500 is excluded (fail fast on real
// server errors); 4xx are client errors and never retried.
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

// Only retry methods with no side effects. We deliberately keep this narrow
// (no PUT/DELETE) so a retry can never duplicate a server-side mutation.
const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);

/**
 * Retries transient failures (network error / 502 / 503 / 504) on safe methods
 * with exponential backoff (1s, 2s, 4s, capped at 8s). Non-retryable statuses
 * fail fast.
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || !RETRYABLE_METHODS.has(req.method)) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        const status = (error as HttpErrorResponse)?.status;
        const isRetryable = status === 0 || RETRYABLE_STATUSES.has(status);
        if (!isRetryable) {
          return throwError(() => error);
        }
        const backoff = Math.min(1000 * 2 ** (retryCount - 1), MAX_BACKOFF_MS);
        return timer(backoff);
      },
    }),
  );
};
